import { CommonActions } from '@react-navigation/native';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { useMutation } from 'react-query';
import { shallow } from 'zustand/shallow';

import useGuestDataQuery from 'app/hooks/useGuestDataQuery';
import { handleOrderAuthToken } from 'app/hooks/useLinkHandling';
import usePushNotifications from 'app/hooks/usePushNotifications';
import useStore, { AuthMode, VerificationState } from 'app/hooks/useStore';
import type { AuthContextProps } from 'app/providers/AuthProvider/AuthProvider.types';
import Analytics from 'app/services/Analytics';
import AuthService from 'app/services/AuthService';
import type { AppClaims } from 'app/services/Firebase.types';
import { getAuthCodeValidition, signInWithEmail } from 'app/services/GuestCenterService';
import Logger from 'app/services/Logger';
import { QueryKeys } from 'app/services/QueryClient';
import { isObject } from 'app/utils/guards';

const MODULE = '[AuthProvider]';

/**
 * Handles user sign out with complete cleanup.
 * Uses a single atomic CommonActions.reset to navigate to Authentication and clear
 * the back stack in one dispatch, avoiding the Android FragmentManager race condition
 * that occurs when two navigation dispatches fire synchronously.
 */
export async function handleSignOut(
  store: Pick<{ reset: () => void }, 'reset'>,
  queryClient: Pick<{ clear: () => void }, 'clear'>,
  navigation: { dispatch: (action: any) => void },
  _handleGoToAuth?: () => void
): Promise<boolean> {
  let didSignOut = true;
  let didClearQueries = true;
  try {
    store.reset();
  } catch (ex) {
    Logger.error(`${MODULE} error resetting store in user sign out`, { ex });
  }

  try {
    await AuthService.signOut();
  } catch (ex) {
    didSignOut = false;
    Logger.error(`${MODULE} error signing out user`, { ex });
  }

  try {
    queryClient.clear();
  } catch (ex) {
    didClearQueries = false;
    Logger.error(`${MODULE} error clearing user data in sign out`, { ex });
  }

  navigation.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [{ name: 'Authentication' }],
    })
  );
  return didSignOut && didClearQueries;
}

export const AuthContext = createContext<AuthContextProps>({
  email: null,
  user: null,
  userClaims: null,
  getToken: () => Promise.resolve(undefined),
  signOut: () => Promise.resolve(),
  handleSignOut: () => Promise.resolve(false),
  isVerified: false,
  isProcessing: false,
  isPending: false,
  isUnverified: false,
  verifyEmail: {
    reset: noop,
    mutate: noop,
    mutateAsync: noop,
  } as AuthContextProps['verifyEmail'],
  verificationState: VerificationState.Unverified,
  verificationProcessing: false,
  verificationMessage: null,
  setVerificationMessage: noop,
  resetVerificationState: noop,
  authCode: '',
  setAuthCode: noop,
  verifyCode: {
    reset: noop,
    mutate: noop,
    mutateAsync: noop,
  } as AuthContextProps['verifyCode'],
});

export type AuthProviderProps = {
  children: ReactNode;
};

export default function AuthProvider({ children }: AuthProviderProps) {
  const pushNotifications = usePushNotifications();
  const { remove: clearGuestData, refetch: refetchGuestData } = useGuestDataQuery({
    notifyOnChangeProps: [],
  });
  const {
    email: $email,
    locale,
    user,
    authMode,
    setVerificationState,
    verificationState,
    verificationProcessing,
    verificationMessage,
    reset,
    resetVerificationState,
    setVerificationMessage,
    emailVerificationPending,
    authCode,
    setAuthCode,
  } = useStore(
    (s) =>
      pick(s, [
        'locale',
        'email',
        'user',
        'authMode',
        'setVerificationState',
        'setEmail',
        'verificationState',
        'verificationProcessing',
        'verificationMessage',
        'reset',
        'setVerificationMessage',
        'resetVerificationState',
        'emailVerificationPending',
        'authCode',
        'setAuthCode',
      ]),
    shallow
  );
  // want the ui to react to claims changes but don't want to persist these in the global store
  const [userClaims, setUserClaims] = useState<AppClaims | null>(null);

  const verifyEmail = useMutation(
    QueryKeys.VerifyEmail,
    async ({ email }: { email: string }) => {
      const res = await signInWithEmail(email, locale);
      if (res.data?.success !== true) throw res;
      return res.data;
    },
    {
      onMutate: ({ email }) => {
        Logger.debug(`${MODULE} verify email sign in starting`, {
          email,
          uid: AuthService.getCurrentUser()?.uid,
        });
      },
      onSettled: () => {
        Logger.debug(`${MODULE} verify email sign in settled`, {});
      },
      onSuccess: (data, { email }) => {
        Logger.debug(`${MODULE} verify email sign in success`, {
          email,
          data,
        });

        emailVerificationPending({ email, authCodeToken: data.codeToken });
      },
      onError: (err, { email }) => {
        Logger.error(`${MODULE} verify email sign in error`, { email, err });
      },
    }
  );

  const verifyCode = useMutation(
    QueryKeys.VerifyCode,
    async (props: { email: string; authCode: string }) => {
      return verifyAuthCode({ ...props, refetchGuestData, clearGuestData });
    },
    {
      onMutate: (props) => {
        Logger.debug(`${MODULE} verify auth code starting`, props);
      },
      onSettled: () => {
        Logger.debug(`${MODULE} verify auth code settled`);
      },
      onSuccess: (data, props) => {
        Logger.debug(`${MODULE} verify auth code success`, { data, props });
      },
      onError: (error, props) => {
        Logger.error(`${MODULE} verify auth code error`, {
          props,
          error,
          cause: isObject(error) && 'cause' in error ? error?.cause : undefined,
        });
      },
    }
  );

  const context = useMemo(
    () => ({
      user,
      userClaims,
      email: $email,
      isVerified: verificationState === VerificationState.Verified,
      isPending: verificationState === VerificationState.Pending,
      isUnverified: verificationState === VerificationState.Unverified,
      isProcessing: verificationProcessing,
      verificationState,
      verificationProcessing,
      verificationMessage,
      getToken: () => AuthService.getToken(),
      signOut: async () => {
        await AuthService.signOut();
        clearGuestData();

        Logger.debug('[AuthProvider] signOut clear guest data');
        const store = useStore.getState();
        store.reset();
      },
      handleSignOut,
      resetVerificationState,
      setVerificationMessage,
      verifyEmail,
      authCode,
      setAuthCode,
      verifyCode,
    }),
    [
      user,
      userClaims,
      $email,
      verificationState,
      verificationProcessing,
      verificationMessage,
      authCode,
      setAuthCode,
      verifyCode,
      resetVerificationState,
      setVerificationMessage,
      verifyEmail,
      clearGuestData,
    ]
  );

  useEffect(() => {
    const unsubscribe = AuthService.subscribe((appUser) => {
      const { user: $user, setUser, setAccessToken } = useStore.getState();

      if (appUser) {
        Logger.debug('[useAuth] signed in', { user: appUser });
        const didUserChange =
          $user?.uid !== appUser.uid || $user?.emailVerified !== appUser.emailVerified;

        AuthService.getTokenResult()
          ?.then(({ claims }: { claims: AppClaims }) => {
            setUserClaims(claims);
          })
          .catch((err: unknown) => Logger.warn('[AuthProvider] getTokenResult failed', { err }));

        if (didUserChange) {
          if (appUser.uid) {
            pushNotifications.onLogin(appUser.uid, appUser.email || '');
            Analytics.setUserId(appUser.uid);
          }

          setUser(appUser);
          AuthService.getToken().then((token: string | undefined) => {
            if (token) setAccessToken(token);
          });
        }

        // Restore state if Firebase confirms user is verified
        const { verificationState: currentVState, email: currentEmail } = useStore.getState();
        if (appUser.emailVerified && currentVState !== VerificationState.Verified) {
          if (!currentEmail && appUser.email) {
            useStore.getState().setEmail(appUser.email);
          }
          useStore.getState().setVerificationState(VerificationState.Verified);
          Logger.debug('[AuthProvider] restored user state', { appUser });
          refetchGuestData();
        }
      } else {
        Logger.debug('[useAuth] signed out');
        if ($user) {
          resetVerificationState();
        }
        setUserClaims(null);
        pushNotifications.onLogout();
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleChange = (nextState: AppStateStatus) => {
        if (nextState === 'active') {
          AuthService.reload()?.then(() => {
            if (AuthService.getCurrentUser()?.emailVerified) {
              setVerificationState(VerificationState.Verified);
            }
          });
        }
      };

      const subscription = AppState.addEventListener('change', handleChange);
      return () => {
        subscription.remove();
      };
    }
    return noop;
  }, [authMode, setVerificationState]);

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
}

export const AUTH_CODE_LENGTH = 6;

export function isValidAuthCode(code: string) {
  return new RegExp(`^\\d{${AUTH_CODE_LENGTH}}$`).test(code);
}

async function verifyAuthCode({
  email,
  authCode,
  refetchGuestData,
  clearGuestData,
}: {
  email: string;
  authCode: string;
  refetchGuestData: () => void;
  clearGuestData: () => void;
}) {
  const { authMode, orderAuthToken, authCodeToken, setVerificationProcessing, setUserVerified } =
    useStore.getState();

  setVerificationProcessing(true);

  try {
    const { data } = await getAuthCodeValidition({
      email,
      code: authCode,
      codeToken: authCodeToken ?? '',
    });

    if (data.success) {
      await AuthService.signIn(data.token);
      setUserVerified();

      // Token Flow: close the loop on updating the email address for tokens representing a travel party that had no email set
      if (authMode === AuthMode.TokenVerifyEmail && orderAuthToken) {
        await handleOrderAuthToken(orderAuthToken, email, clearGuestData);
      }

      refetchGuestData();
      return data;
    }
    throw data;
  } catch (err) {
    throw new Error('failed to verify auth code', {
      cause: err,
    });
  } finally {
    setVerificationProcessing(false);
  }
}
